import CompanyService from "src/Services/API/company.service";

// The company's own wording for Makani's core concepts, chosen during
// onboarding. Cached in localStorage because it is read on nearly every render
// (menus, headers, buttons) and changes about once in a tenant's lifetime.
const VOCAB_KEY = "vocabulary";

// The wording a company gets until it says otherwise.
const DEFAULTS = {
  workOrder: "Work Order",
  serviceCall: "Service Call",
  tech: "Technician",
  asset: "Asset",
  truckStock: "Truck Stock",
  recommendation: "Recommendation",
};

export const getVocabulary = () => {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(VOCAB_KEY)) || {}) };
  } catch (e) {
    return { ...DEFAULTS };
  }
};

export const setVocabulary = (vocabulary) => {
  localStorage.setItem(VOCAB_KEY, JSON.stringify(vocabulary || {}));
};

export const clearVocabulary = () => {
  localStorage.removeItem(VOCAB_KEY);
};

// Fetch the wording for the company the user is acting as. Failing is not fatal
// — the defaults stand — so callers don't need to guard the app on it.
export const loadVocabulary = async () => {
  try {
    const response = await CompanyService.getVocabulary();
    setVocabulary(response.data || {});
  } catch (e) {
    console.log("failed to load vocabulary: ", e);
  }
};

/** The company's word for a concept, e.g. term("workOrder") → "Job". */
export const term = (key) => getVocabulary()[key] || DEFAULTS[key] || "";

/**
 * The plural of that word, for list pages and menus. English-only and naive on
 * purpose: the words are short nouns the customer typed ("Job", "Event",
 * "Trouble Call"), so a proper inflection library would be all cost, no gain.
 */
export const termPlural = (key) => pluralize(term(key));

const pluralize = (word) => {
  if (!word) return "";
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
};

// The three concepts that carry a per-tenant word, by their default English
// spelling, plus the abbreviations the UI uses for them ("WO #", "WO number").
// Longest first so "Work Orders" is matched before "Work Order".
const RENAMEABLE = [
  ["workOrder", "Work Order", ["WO"]],
  ["asset", "Asset", []],
  ["recommendation", "Recommendation", []],
];

// Match the tenant's word to the case of the text it is replacing, so a
// mid-sentence "work order" stays lowercase and a heading "WORK ORDER" stays
// upper. Title Case is the stored form, so it is the default.
const matchCase = (replacement, matched) => {
  if (matched === matched.toUpperCase()) return replacement.toUpperCase();
  if (matched === matched.toLowerCase()) return replacement.toLowerCase();
  return replacement;
};

/**
 * Rewrite the tenant's words into a finished English string, so compound copy
 * like "View Work Order Attachment" or "No work orders found" gets relabelled
 * without the call site having to interpolate term() by hand.
 *
 * The canonical English is kept in the code and rewritten here at render time —
 * which is also why a constant array's strings can be vocabularized when they
 * are displayed, not frozen at module load.
 *
 * Whole word only (word boundaries), singular and plural, case-preserving.
 */
export const vocabularize = (text) => {
  if (!text || typeof text !== "string") return text;
  let out = text;
  for (const [key, english, abbreviations = []] of RENAMEABLE) {
    const singular = term(key);
    const plural = termPlural(key);
    if (singular === english && plural === pluralize(english)) continue; // default → nothing to do
    out = out.replace(new RegExp(`\\b${pluralize(english)}\\b`, "gi"), (m) => matchCase(plural, m));
    out = out.replace(new RegExp(`\\b${english}\\b`, "gi"), (m) => matchCase(singular, m));
    // Abbreviations ("WO #") only make sense for the default noun, so once the
    // tenant renamed it they get the tenant's word. Case-sensitive on purpose:
    // "WO" is a deliberate token; the term keeps its stored casing (no
    // matchCase — uppercasing "Job" into "JOB" would be wrong for "WO #").
    for (const abbr of abbreviations) {
      out = out.replace(new RegExp(`\\b${abbr}s\\b`, "g"), plural);
      out = out.replace(new RegExp(`\\b${abbr}\\b`, "g"), singular);
    }
    // CamelCase compound names ("WorkOrderFullAccess", seeded policy names)
    // carry the concept with the space removed, so word boundaries can't see
    // it. Substring-replace the spaceless form with the spaceless term.
    const camel = english.replace(/\s+/g, "");
    if (camel !== english) {
      out = out.split(`${camel}s`).join(plural.replace(/\s+/g, ""));
      out = out.split(camel).join(singular.replace(/\s+/g, ""));
    }
  }
  return out;
};
